import { useNavigate } from 'react-router-dom'
import type { UpcomingReservationsCarouselComponent } from '@/types/sdui'
import { Body, Caption } from '@meiuca/ui-components'
import { Calendar, MapPin } from 'lucide-react'
import { formatDateForUI, formatTimeForUI } from '@meiuca/web-foundation'
import { useComponentImpression, analyticsService } from '@meiuca/analytics'

interface UpcomingReservationsCarouselProps {
  config: UpcomingReservationsCarouselComponent
}

export function UpcomingReservationsCarousel({ config }: UpcomingReservationsCarouselProps) {
  const navigate = useNavigate()

  // Track component impression
  const impressionRef = useComponentImpression({
    componentType: 'upcoming_reservations_carousel',
    componentKey: config.id
  })

  const handleReservationClick = (id: string, venueName: string) => {
    // Track reservation click
    analyticsService.track('upcoming_reservations_carousel_card_clicked', {
      component_id: config.id,
      component_type: 'upcoming_reservations_carousel',
      reservation_id: id,
      venue_name: venueName,
      target_url: `/reservations/${id}`
    })

    navigate(`/reservations/${id}`)
  }

  const handleCtaClick = () => {
    if (config.ctaLink) {
      // Track CTA click
      analyticsService.track('upcoming_reservations_carousel_see_all_clicked', {
        component_id: config.id,
        component_type: 'upcoming_reservations_carousel',
        target_url: config.ctaLink
      })

      navigate(config.ctaLink)
    }
  }

  const openMap = (lat: number, lng: number, venueName: string, e: React.MouseEvent) => {
    e.stopPropagation()

    // Track map click
    analyticsService.track('upcoming_reservations_carousel_map_clicked', {
      component_id: config.id,
      component_type: 'upcoming_reservations_carousel',
      venue_name: venueName,
      latitude: lat,
      longitude: lng
    })

    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    window.open(url, '_blank')
  }

  return (
    <div ref={impressionRef} className="w-full bg-background-primary py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Body className="text-text-primary font-semibold">{config.title}</Body>
          {config.ctaText && config.ctaLink && (
            <button
              onClick={handleCtaClick}
              className="text-brand-primary hover:text-brand-primary/80 transition-colors text-sm font-medium"
            >
              {config.ctaText} →
            </button>
          )}
        </div>

        {/* Carousel */}
        <div className="overflow-x-auto -mx-4 px-4">
          <div className="flex gap-4 pb-4">
            {config.reservations.map((reservation) => (
              <button
                key={reservation.id}
                onClick={() => handleReservationClick(reservation.id, reservation.venueName)}
                className="flex-shrink-0 w-80 bg-surface-primary rounded-lg border border-border-primary hover:border-brand-primary transition-all p-4 text-left"
              >
                {/* Date and Time */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-1 px-2 py-1 bg-brand-primary/10 rounded-md">
                    <Calendar className="w-4 h-4 text-brand-primary" />
                    <Caption className="text-brand-primary font-medium">
                      {formatDateForUI(reservation.date, 'short')} • {formatTimeForUI(reservation.date)}
                    </Caption>
                  </div>
                </div>

                {/* Venue Info */}
                <div className="flex items-start gap-3 mb-3">
                  {reservation.venueLogo ? (
                    <img
                      src={reservation.venueLogo}
                      alt={reservation.venueName}
                      className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-surface-secondary flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-text-tertiary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <Body className="text-text-primary font-medium line-clamp-1">
                      {reservation.venueName}
                    </Body>
                    <Caption className="text-text-secondary whitespace-pre-wrap line-clamp-2">
                      {reservation.venueAddress}
                    </Caption>
                  </div>
                </div>

                {/* Map Link */}
                {reservation.venueLatitude && reservation.venueLongitude && (
                  <div className="pt-3 border-t border-border-primary">
                    <button
                      onClick={(e) => openMap(reservation.venueLatitude!, reservation.venueLongitude!, reservation.venueName, e)}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-surface-secondary hover:bg-surface-tertiary rounded-md transition-colors"
                    >
                      <MapPin className="w-4 h-4 text-text-secondary" />
                      <Caption className="text-text-secondary font-medium">Ver no mapa</Caption>
                    </button>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Empty State */}
        {config.reservations.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
            <Body className="text-text-secondary">
              Você não tem reservas futuras no momento
            </Body>
          </div>
        )}
      </div>
    </div>
  )
}
